import type { CompanyUpdatePayload } from '../../types/company';
import { DEFAULT_SUPPORT_PROGRAM, isDefaultSupportProgram } from '../../config/defaultSupportProgram';

/**
 * Formulaire réduit d'une AVEC (cahier des charges 4.4). Le nom du groupe va
 * dans `raisonSociale` ; tout le reste, lieu compris, dans la fiche
 * `avecDetails` de l'entreprise (customer-service AvecDetailsDto), que le
 * serveur fusionne à chaque mise à jour.
 */
export interface AvecFormValues {
  raisonSociale: string;
  memberCount: number | string;
  province: string;
  territoire: string;
  chefferie: string;
  village: string;
  /** Programme choisi dans la liste (institution variante programme d'appui). */
  supportProgramInstitutionId: string;
  /** Nom lisible ; saisi à la main seulement si le programme n'est pas listé. */
  supportProgramName: string;
  creationDate: string;
  registrationNumber: string;
  presidentPhone: string;
  /**
   * Email de contact de l'AVEC (companies.email, pas la fiche). Par défaut
   * celui du compte connecté, s'il est vérifié ; facultatif.
   */
  email?: string;
}

/** Fiche envoyée : null efface une valeur (le serveur fusionne la fiche). */
export type AvecDetails = { [K in (typeof TEXT_FIELDS)[number]]?: string | null } & {
  memberCount?: number | null;
};

export const EMPTY_AVEC_VALUES: Omit<AvecFormValues, 'raisonSociale'> = {
  memberCount: '',
  province: '',
  territoire: '',
  chefferie: '',
  village: '',
  supportProgramInstitutionId: '',
  supportProgramName: '',
  creationDate: '',
  registrationNumber: '',
  presidentPhone: '',
};

const TEXT_FIELDS = [
  'province',
  'territoire',
  'chefferie',
  'village',
  'supportProgramInstitutionId',
  'supportProgramName',
  'creationDate',
  'registrationNumber',
  'presidentPhone',
] as const;

/** Chaîne vide -> undefined : le serveur refuse '' pour une date facultative. */
const clean = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const text = String(value).trim();
  return text === '' ? undefined : text;
};

export function parseMemberCount(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : parseInt(String(value ?? '').trim(), 10);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
}

/**
 * Fiche AVEC prête à partir : jamais de chaîne vide (refusée pour une date),
 * null pour un champ vidé afin que la fusion côté serveur l'efface, membres
 * en entier.
 */
export function buildAvecDetails(values: AvecFormValues): AvecDetails {
  const details: AvecDetails = {};
  for (const field of TEXT_FIELDS) {
    details[field] = clean(values[field]) ?? null;
  }
  details.memberCount = parseMemberCount(values.memberCount) ?? null;
  return details;
}

export function buildAvecCompanyPayload(values: AvecFormValues): CompanyUpdatePayload {
  // Email : envoyé seulement s'il est renseigné (jamais '', refusé par le
  // serveur, et jamais d'effacement d'un email déjà enregistré).
  const email = clean(values.email);
  return {
    variant: 'avec',
    raisonSociale: values.raisonSociale.trim(),
    ...(email ? { email } : {}),
    avecDetails: buildAvecDetails(values),
  };
}

/** Email du compte, seulement s'il est vérifié : proposé comme contact par défaut. */
export function verifiedAccountEmail(user: { email?: string | null; emailVerified?: boolean } | null | undefined): string {
  const email = (user?.email || '').trim().toLowerCase();
  return user?.emailVerified === true && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : '';
}

/** Relit la fiche AVEC renvoyée avec l'entreprise. */
export function readAvecDetails(raw: unknown): Omit<AvecFormValues, 'raisonSociale'> {
  const d = (raw ?? {}) as Record<string, unknown>;
  const text = (v: unknown) => (typeof v === 'string' ? v : '');
  return {
    memberCount: typeof d.memberCount === 'number' ? d.memberCount : '',
    province: text(d.province),
    territoire: text(d.territoire),
    chefferie: text(d.chefferie),
    village: text(d.village),
    supportProgramInstitutionId: text(d.supportProgramInstitutionId),
    supportProgramName: text(d.supportProgramName),
    creationDate: text(d.creationDate).slice(0, 10),
    registrationNumber: text(d.registrationNumber),
    presidentPhone: text(d.presidentPhone),
  };
}

/**
 * Programme d'appui de l'AVEC, facultatif : sans choix, l'AVEC est suivie
 * par la structure d'appui par défaut (« Réseau ikiotahub »), et peut en
 * changer à tout moment. Renvoie le programme qui figurera sur les pièces.
 */
export function effectiveSupportProgram(
  values: Pick<AvecFormValues, 'supportProgramInstitutionId' | 'supportProgramName'>,
): { institutionId: string; name: string; isDefault: boolean } {
  const id = values.supportProgramInstitutionId.trim();
  const name = values.supportProgramName.trim();
  if (id || name) return { institutionId: id, name, isDefault: isDefaultSupportProgram(id) };
  return { institutionId: DEFAULT_SUPPORT_PROGRAM.id, name: DEFAULT_SUPPORT_PROGRAM.name, isDefault: true };
}
